import { createApi } from '@reduxjs/toolkit/query/react';
import { customBaseQuery } from '@/lib/baseQuery';

import { ApiResponse, UserProfile } from '@/types';
import {
  mapObjectKeysToCamelCase,
  mapObjectKeysToSnakeCase,
} from '@/utils/mapper';

export type UserProfileResponse = {
  common_data: UserProfile;
};
export const userApi = createApi({
  reducerPath: 'userApi',
  baseQuery: customBaseQuery,
  endpoints: (builder) => ({
    fetchRegisterType: builder.query<any, void>({
      query: () => '/api/user/register-type/',
      transformResponse: (response: ApiResponse<any>) => {
        return mapObjectKeysToCamelCase(response);
      },
    }),
    fetchUserProfile: builder.query<ApiResponse<UserProfileResponse>, void>({
      query: () => 'api/user/profile/',
      transformResponse: (response: ApiResponse<any>) => {
        response.data.common_data = mapObjectKeysToCamelCase(
          response.data.common_data,
        );
        return response;
      },
    }),
    fetchUserData: builder.query<any, void>({
      query: () => 'api/user/me/',
      transformResponse: (response: ApiResponse<any>) => {
        response.data = mapObjectKeysToCamelCase(response.data);
        return mapObjectKeysToCamelCase(response);
      },
    }),
    postUserData: builder.mutation<ApiResponse<any>, any>({
      query: (body) => ({
        url: 'api/user/me/',
        method: 'POST',
        body,
      }),
    }),
    postUserProfile: builder.mutation<ApiResponse<any>, UserProfile>({
      query: (body) => {
        const transformedData = mapObjectKeysToSnakeCase(body);
        return {
          url: '/api/user/profile/update/',
          method: 'PATCH',
          body: {
            common_data: transformedData,
          },
        };
      },
    }),
    createProfile: builder.mutation<ApiResponse<any>, UserProfile>({
      query: (body) => {
        const transformedData = mapObjectKeysToSnakeCase(body);
        return {
          url: 'api/user/profile/create/',
          method: 'POST',
          body: transformedData,
        };
      },
    }),
    updateProfile: builder.mutation<ApiResponse<any>, UserProfile>({
      query: (body) => {
        const transformedData = mapObjectKeysToSnakeCase(body);
        return {
          url: 'api/user/profile/update/',
          method: 'POST',
          body: transformedData,
        };
      },
    }),
    verifyPhone: builder.mutation<ApiResponse<{ message: string }>, void>({
      query: () => {
        return {
          url: 'api/auth/verify-phone/',
          method: 'POST',
        };
      },
    }),
    verifyOTP: builder.mutation<
      ApiResponse<{ verification_status: string }>,
      { otp: string }
    >({
      query: (body) => {
        return {
          url: 'api/auth/verify-otp/',
          method: 'POST',
          body,
        };
      },
    }),
    contactBroker: builder.mutation({
      query: (body) => {
        return {
          url: 'api/core/talk-to-broker/',
          method: 'POST',
        };
      },
    }),
  }),
});
export const {
  useFetchUserProfileQuery,
  useLazyFetchUserProfileQuery,
  usePostUserProfileMutation,
  useFetchUserDataQuery,
  usePostUserDataMutation,
  useCreateProfileMutation,
  useUpdateProfileMutation,
  useFetchRegisterTypeQuery,
  useVerifyPhoneMutation,
  useVerifyOTPMutation,
  useContactBrokerMutation,
} = userApi;
